import React, { useState, useEffect } from 'react';
import { getPromotionsAll, createPromotion, updatePromotion, deletePromotion } from '../api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const empty = {
  title: '', subtitle: '', type: 'poster',
  media_url: '', cta_text: 'Get Started', cta_link: '/contact',
  badge_text: '', sort_order: 0, is_active: 1,
};

const Modal = ({ title, onClose, children }) => (
  <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className="admin-modal" style={{ maxWidth: 640 }}>
      <div className="admin-modal-header">
        <h3>{title}</h3>
        <button className="admin-modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="admin-modal-body">{children}</div>
    </div>
  </div>
);

const PromotionsAdmin = () => {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [form,    setForm]    = useState(empty);
  const [mediaFile,    setMediaFile]    = useState(null);
  const [mediaPreview, setMediaPreview] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [msg,     setMsg]     = useState('');
  const [fileInputKey, setFileInputKey] = useState(0); // forces file input to reset

  const load = async () => {
    setLoading(true);
    const res = await getPromotionsAll();
    if (res.success) setItems(res.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm(empty); setMediaFile(null); setMediaPreview('');
    setFileInputKey(k => k + 1);
    setModal('add'); setMsg('');
  };
  const openEdit = (p) => {
    setForm({ ...p });
    setMediaFile(null);
    setFileInputKey(k => k + 1);
    const preview = p.media_url
      ? (p.media_url.startsWith('http') ? p.media_url : `${API_BASE}${p.media_url}`)
      : '';
    setMediaPreview(preview);
    setModal('edit'); setMsg('');
  };
  const closeModal = () => { setModal(null); setForm(empty); setMediaFile(null); setMediaPreview(''); setFileInputKey(k => k + 1); };

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
    setForm(f => ({ ...f, media_url: '' }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setMsg('Title is required.'); return; }
    setSaving(true); setMsg(''); setUploadPct(0);
    try {
      const fd = new FormData();
      // Append all form fields except 'id' (passed in URL for PUT)
      const skip = new Set(['id']);
      Object.entries(form).forEach(([k, v]) => {
        if (!skip.has(k) && v !== null && v !== undefined) fd.append(k, v);
      });
      if (mediaFile) fd.append('media', mediaFile);

      const onProg = mediaFile ? (pct) => setUploadPct(pct) : null;
      const res = modal === 'add'
        ? await createPromotion(fd, onProg)
        : await updatePromotion(form.id, fd, onProg);
      if (res.success) { await load(); closeModal(); }
      else setMsg(res.message || 'Error saving.');
    } catch (err) { setMsg(err.message || 'Network error. Please try again.'); }
    setSaving(false); setUploadPct(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    await deletePromotion(id);
    await load();
  };

  const isVideo = (url) => url && (url.endsWith('.mp4') || url.endsWith('.webm') || url.includes('youtube') || url.includes('vimeo'));

  return (
    <div>
      <div className="admin-section-header">
        <h2>Promotions</h2>
        <button className="btn-admin-primary" onClick={openAdd}>+ Add Promotion</button>
      </div>

      {loading ? <p>Loading...</p> : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>#</th><th>Preview</th><th>Title</th><th>Type</th><th>CTA</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {items.map((p, i) => (
                <tr key={p.id}>
                  <td>{i + 1}</td>
                  <td>
                    {p.media_url ? (
                      isVideo(p.media_url) ? (
                        <div style={{ width:60, height:40, background:'#07071a', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center' }}>
                          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="white" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                        </div>
                      ) : (
                        <img src={p.media_url.startsWith('http') ? p.media_url : `${API_BASE}${p.media_url}`}
                          alt="" className="img-preview" style={{ width:60, height:40, objectFit:'cover', borderRadius:6 }}
                          onError={e => e.target.style.display='none'} />
                      )
                    ) : (
                      <div style={{ width:60, height:40, background:'linear-gradient(135deg,#16165a,#e8603a)', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                      </div>
                    )}
                  </td>
                  <td style={{ fontWeight:600, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.title}</td>
                  <td>
                    <span className={`status-badge ${p.type === 'video' ? 'status-new' : 'status-active'}`}>
                      {p.type === 'video' ? 'Video' : 'Poster'}
                    </span>
                  </td>
                  <td style={{ fontSize:'0.8rem', color:'#6c757d' }}>{p.cta_text}</td>
                  <td>
                    <span className={`status-badge ${p.is_active ? 'status-active' : 'status-inactive'}`}>
                      {p.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      <button className="btn-admin-primary btn-admin-sm" onClick={() => openEdit(p)}>✏️ Edit</button>
                      <button className="btn-admin-primary btn-admin-sm btn-admin-danger" onClick={() => handleDelete(p.id)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={7} style={{ textAlign:'center', color:'#adb5bd', padding:'40px' }}>No promotions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={modal === 'add' ? '+ Add Promotion' : '✏️ Edit Promotion'} onClose={closeModal}>
          {msg && <div style={{ background:'#fef2f2', color:'#991b1b', padding:'10px 14px', borderRadius:8, marginBottom:14, fontSize:'0.85rem' }}>❌ {msg}</div>}
          <form onSubmit={handleSave}>

            {/* Type selector */}
            <div className="admin-form-group">
              <label>Type</label>
              <div style={{ display:'flex', gap:8 }}>
                {[{v:'poster', label:'Poster', icon:<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>},
                  {v:'video',  label:'Video',  icon:<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>}
                ].map(opt => (
                  <button key={opt.v} type="button"
                    onClick={() => setForm(f => ({ ...f, type: opt.v }))}
                    style={{ padding:'8px 20px', borderRadius:8, border:'2px solid', cursor:'pointer', fontSize:'0.875rem', fontWeight:700, display:'flex', alignItems:'center', gap:6, transition:'all 0.15s',
                      background: form.type === opt.v ? '#16165a' : 'white',
                      borderColor: form.type === opt.v ? '#16165a' : '#dee2e6',
                      color: form.type === opt.v ? 'white' : '#6c757d',
                    }}>
                    {opt.icon}{opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div className="admin-form-group">
              <label>Title *</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Promotion headline" required />
            </div>

            {/* Subtitle */}
            <div className="admin-form-group">
              <label>Subtitle / Description</label>
              <textarea value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} rows={2} placeholder="Supporting text..." />
            </div>

            {/* Badge */}
            <div className="admin-form-group">
              <label>Badge Text <span style={{ color:'#9ca3af', fontWeight:400 }}>(optional pill, e.g. "Special Offer")</span></label>
              <input value={form.badge_text} onChange={e => setForm({ ...form, badge_text: e.target.value })} placeholder="Special Offer" maxLength={80} />
            </div>

            {/* CTA */}
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label>CTA Button Text</label>
                <input value={form.cta_text} onChange={e => setForm({ ...form, cta_text: e.target.value })} placeholder="Get Started" />
              </div>
              <div className="admin-form-group">
                <label>CTA Link</label>
                <input value={form.cta_link} onChange={e => setForm({ ...form, cta_link: e.target.value })} placeholder="/contact#quote" />
              </div>
            </div>

            {/* Media upload */}
            <div className="admin-form-group" style={{ background:'#f8f9fa', borderRadius:10, padding:'14px 16px', border:'1.5px solid #e9ecef' }}>
              <label style={{ fontWeight:700, color:'#16165a', marginBottom:8, display:'block' }}>
                {form.type === 'video' ? '▶ Video File / URL' : '🖼 Poster Image'}
              </label>
              <input
                key={fileInputKey}
                type="file"
                accept={form.type === 'video' ? 'video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo' : 'image/jpeg,image/jpg,image/png,image/gif,image/webp'}
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (form.type === 'video' && file.size > 200 * 1024 * 1024) {
                    setMsg(`⚠️ Large file (${(file.size/1024/1024).toFixed(0)} MB). Max 200MB. Consider using YouTube/Vimeo URL for faster uploads.`);
                  } else {
                    setMsg('');
                  }
                  handleMediaChange(e);
                }}
                style={{ padding:'8px', border:'1.5px dashed #dee2e6', borderRadius:8, cursor:'pointer', width:'100%' }}
              />
              {mediaFile && (
                <div style={{ marginTop:8, fontSize:'0.8rem', color:'#27ae60', display:'flex', alignItems:'center', gap:6 }}>
                  ✅ {mediaFile.name}
                  <button type="button" onClick={() => { setMediaFile(null); setMediaPreview(''); setFileInputKey(k => k + 1); }} style={{ background:'none', border:'none', cursor:'pointer', color:'#e74c3c', fontSize:'0.75rem' }}>✕</button>
                </div>
              )}
              {/* Preview */}
              {mediaPreview && !mediaFile && (
                <div style={{ marginTop:10 }}>
                  {isVideo(mediaPreview)
                    ? <video controls style={{ width:'100%', maxHeight:160, borderRadius:8 }}><source src={mediaPreview}/></video>
                    : <img src={mediaPreview} alt="preview" style={{ width:'100%', maxHeight:160, objectFit:'cover', borderRadius:8 }} onError={e=>e.target.style.display='none'} />
                  }
                </div>
              )}
              {mediaPreview && mediaFile && form.type === 'video' && (
                <video controls style={{ marginTop:8, width:'100%', maxHeight:160, borderRadius:8 }}><source src={mediaPreview}/></video>
              )}
              {mediaPreview && mediaFile && form.type === 'poster' && (
                <img src={mediaPreview} alt="preview" style={{ marginTop:8, width:'100%', maxHeight:160, objectFit:'cover', borderRadius:8 }} />
              )}
              {/* URL fallback for videos */}
              {form.type === 'video' && !mediaFile && (
                <div style={{ marginTop:10 }}>
                  <label style={{ fontSize:'0.8rem', fontWeight:600, color:'#6c757d' }}>Or paste YouTube/Vimeo URL:</label>
                  <input value={form.media_url} onChange={e => setForm({ ...form, media_url: e.target.value })} placeholder="https://youtube.com/watch?v=..."
                    style={{ marginTop:4, width:'100%', padding:'8px 12px', border:'1.5px solid #dee2e6', borderRadius:8, fontSize:'0.85rem' }} />
                </div>
              )}
            </div>

            {/* Sort + Active */}
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label>Sort Order</label>
                <input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: +e.target.value })} />
              </div>
              <div className="admin-form-group" style={{ display:'flex', alignItems:'center', gap:10, paddingTop:28 }}>
                <label className="admin-switch">
                  <input type="checkbox" checked={!!form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} />
                  <span className="admin-slider"></span>
                </label>
                <span style={{ fontSize:'0.88rem', fontWeight:600 }}>Active</span>
              </div>
            </div>

            <div style={{ marginTop:8 }}>
              {/* Upload progress for video files */}
              {saving && mediaFile && (
                <div style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.8rem', fontWeight:600, color:'#16165a', marginBottom:6 }}>
                    <span>⬆️ Uploading...</span>
                    <span>{uploadPct}%</span>
                  </div>
                  <div style={{ width:'100%', height:7, background:'#e9ecef', borderRadius:4, overflow:'hidden' }}>
                    <div style={{
                      height:'100%',
                      width:`${uploadPct}%`,
                      background: uploadPct===100 ? 'linear-gradient(90deg,#27ae60,#2ecc71)' : 'linear-gradient(90deg,#16165a,#e8603a)',
                      borderRadius:4,
                      transition:'width 0.3s ease',
                    }} />
                  </div>
                  {uploadPct > 0 && uploadPct < 100 && (
                    <p style={{ fontSize:'0.72rem', color:'#6c757d', marginTop:4 }}>Please wait — do not close this window</p>
                  )}
                </div>
              )}
              <div style={{ display:'flex', gap:10 }}>
                <button type="submit" className="btn-admin-primary" disabled={saving} style={{ flex:1, justifyContent:'center', padding:'12px' }}>
                  {saving
                    ? (mediaFile ? `⬆️ Uploading... ${uploadPct}%` : '⏳ Saving...')
                    : '💾 Save Promotion'}
                </button>
                <button type="button" className="btn-admin-outline" onClick={closeModal} disabled={saving}>Cancel</button>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default PromotionsAdmin;
